/**
 * Live TV Watch — channel-detail page with EPG alongside the player.
 *
 * Layout (lg+):  ┌───── Now Playing ─────┐ ┌──── EPG ────┐
 *                │  Logo + name + plot   │ │ now ▸       │
 *                │  ▶ Play live           │ │ next        │
 *                │  current programme     │ │ later…       │
 *                └───────────────────────┘ │ past (catch) │
 *                                          └─────────────┘
 *
 * Tapping ▶ launches the bundled native ExoPlayer (full-screen). Past
 * programmes with archive support launch the catchup URL. The player
 * supports Picture-in-Picture so users can shrink it and keep browsing
 * the guide here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import Hls from "hls.js";
import mpegts from "mpegts.js";
import PosterImage from "../components/PosterImage";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { isElectron } from "../lib/deviceId";
import { webUrl } from "../lib/webFetch";
import { makeProxyLoader } from "../lib/hlsProxyLoader";
import {
  isNativePlayerAvailable,
  playWithNativeExoPlayer,
  playInlineNativeExoPlayer,
  updateInlineNativeRect,
  stopInlineNativeExoPlayer,
} from "../lib/nativePlayer";
import { buildXtreamCatchupUrl } from "../lib/xtreamApi";
import { currentProgramme, getChannelProgrammes } from "../lib/epgParser";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Play,
  Maximize2,
  Tv,
  Calendar,
  Clock,
  History,
  Loader2,
  Star,
} from "lucide-react";
import {
  isFavorited,
  toggleFavorite,
  isFavoritable,
} from "../lib/favorites";
import { addToHistory } from "../lib/watchHistory";
import { isTvLayout } from "../lib/tvDetect";

const HOURS_WINDOW = 24; // show 24h around "now"

function fmtTime(d) {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function fmtDay(d) {
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}
function dayKey(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
}

export default function LiveTvWatch() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const outletCtx = useOutletContext() || {};
  const playlists = Array.isArray(outletCtx.playlists) ? outletCtx.playlists : [];
  const { channels, epgByChannel, xtream, status, error, reload, source } = usePlaylistData();

  const channel = useMemo(
    () => {
      // v1.4.0 — Look up by id first (fast path for M3U-parsed
      // channels), then fall back to xc_id / stream_url so
      // Xtream-only channels (rows returned by the Xtream JSON API
      // that aren't in the M3U) still resolve. Prevents the
      // "Channel not found" trap when the LiveTv page merged
      // Xtream + M3U and this route received an Xtream numeric id.
      const byId = channels.find((c) => c.id === channelId) || null;
      if (byId) return byId;
      if (!channelId) return null;
      return (
        channels.find((c) => String(c.xc_id || "") === String(channelId)) ||
        channels.find((c) => c.stream_url === channelId) ||
        null
      );
    },
    [channels, channelId]
  );

  // Ordered list of live channels in the SAME category (group) as the
  // current channel — so Prev / Next stays within the user's mental model.
  const channelNav = useMemo(() => {
    const liveOnly = channels.filter((c) => c.content_type === "live_tv");
    const sameGroup = channel?.group
      ? liveOnly.filter((c) => c.group === channel.group)
      : liveOnly;
    const list = sameGroup.length > 1 ? sameGroup : liveOnly;
    const idx = list.findIndex((c) => c.id === channelId);
    if (idx < 0) return { prev: null, next: null, position: 0, total: list.length, list, index: 0 };
    const prev = list[(idx - 1 + list.length) % list.length] || null;
    const next = list[(idx + 1) % list.length] || null;
    return { prev, next, position: idx + 1, total: list.length, list, index: idx };
  }, [channels, channel, channelId]);

  // v1.1.2 — Remote / keyboard shortcuts for channel zapping in the
  // fullscreen Live TV view. Maps to what typical Smart-TV remotes
  // and Bluetooth media keys send:
  //   ▶▶ / MediaTrackNext / ChannelDown / PageDown → next channel
  //   ◀◀ / MediaTrackPrevious / ChannelUp / PageUp → previous channel
  // Native Android handles these inside ExoPlayerActivity; this
  // handler covers the web-fullscreen path and any keys that leak
  // through the WebView on TV browsers.
  useEffect(() => {
    const onKey = (e) => {
      const nextKeys = new Set([
        "MediaTrackNext",
        "MediaFastForward",
        "ChannelDown",
        "PageDown",
      ]);
      const prevKeys = new Set([
        "MediaTrackPrevious",
        "MediaRewind",
        "ChannelUp",
        "PageUp",
      ]);
      if (nextKeys.has(e.key) && channelNav.next) {
        e.preventDefault();
        navigate(`/player/live/${encodeURIComponent(channelNav.next.id)}`, {
          replace: true,
        });
        return;
      }
      if (prevKeys.has(e.key) && channelNav.prev) {
        e.preventDefault();
        navigate(`/player/live/${encodeURIComponent(channelNav.prev.id)}`, {
          replace: true,
        });
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [channelNav.next, channelNav.prev, navigate]);

  // v1.3.81 — Compact { stream_url, name } queue passed to the native
  // ExoPlayer so it can enable D-pad Up/Down zapping + PreloadConfig.
  // Capped at 500 channels to keep the Intent extra reasonably sized
  // (Android's Binder transaction limit is 1 MB total).
  const nativeChannelQueue = useMemo(() => {
    const list = channelNav.list || [];
    if (list.length < 2) return null;
    const capped = list.slice(0, 500);
    const queue = capped
      .filter((c) => !!c.stream_url)
      .map((c) => ({ stream_url: c.stream_url, name: c.name || "" }));
    const startIdx = Math.max(
      0,
      queue.findIndex((c) => c.stream_url === channel?.stream_url)
    );
    return { queue, startIdx };
  }, [channelNav.list, channel?.stream_url]);

  const showFav = channel ? isFavoritable("live_tv") : false;
  const [favTick, setFavTick] = useState(0);
  const fav = channel ? isFavorited("live_tv", channel.stream_url) : false;
  const [launching, setLaunching] = useState(false);
  // v1.4.2 — Stable refs so the auto-launch effect (which reruns on
  // channel change) can call the latest `playLiveFullscreen` without
  // adding it to the deps array (which would recreate the effect and
  // re-launch mpv every time we set an error state).
  const launchingRef = useRef(false);
  const playLiveFullscreenRef = useRef(null);
  useEffect(() => { launchingRef.current = launching; }, [launching]);
  // Smart-TV compact mode — caches the result so we don't re-detect on
  // every render. Drives all the "make it smaller" branches below.
  const tvLayout = useMemo(() => isTvLayout(), []);

  // Full EPG window (past 6h + future 18h). Uses the multi-key lookup
  // (`tvg_id` → `tvg_name` → normalised channel name) so EPG still
  // shows up even when the M3U `tvg-id` doesn't match the XMLTV
  // `channel id`, which is the #1 cause of "EPG not showing".
  const epgWindow = useMemo(() => {
    if (!channel) return [];
    const all = getChannelProgrammes(epgByChannel, channel);
    const nowTs = Math.floor(Date.now() / 1000);
    const startWin = nowTs - 6 * 3600;
    const endWin = nowTs + (HOURS_WINDOW - 6) * 3600;
    return all
      .filter((p) => p.stop_ts > startWin && p.start_ts < endWin)
      .sort((a, b) => a.start_ts - b.start_ts);
  }, [channel, epgByChannel]);

  const nowProg = useMemo(
    () => (channel ? currentProgramme(epgByChannel, channel) : null),
    [channel, epgByChannel]
  );

  // Group EPG by day for a cleaner timeline
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const p of epgWindow) {
      const k = dayKey(p.start_ts);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(p);
    }
    return Array.from(buckets.entries());
  }, [epgWindow]);

  const launchExoPlayer = useCallback(
    async (url, title) => {
      if (!url) return;
      addToHistory({
        stream_url: url,
        name: title,
        logo_url: channel?.logo_url || null,
        content_type: "live_tv",
      });      // CRITICAL: stop the inline surface BEFORE launching the fullscreen
      // ExoPlayerActivity. Without this we get two players running — the
      // inline one still draining the network + audio, and the new
      // fullscreen one on top. Symptom: double audio + jittery video.
      try {
        await stopInlineNativeExoPlayer();
      } catch {
        /* no-op */
      }
      if (isElectron() && window.electronAPI?.playWithMpv) {
        setLaunching(true);
        try {
          await window.electronAPI.playWithMpv(url, { title });
        } finally {
          setLaunching(false);
        }
        return;
      }
      if (isNativePlayerAvailable()) {
        setLaunching(true);
        try {
          // v1.3.98 — Compute current/next EPG programmes so the native
          // ExoPlayer activity can render the same channel logo + group
          // + "current  NEXT: next" panel that LiveTvOverlay draws on
          // web.
          const nowProgN = currentProgramme(epgByChannel, channel);
          const nextProgN = (() => {
            if (!channel || !nowProgN?.stop) return null;
            const all = getChannelProgrammes(epgByChannel, channel);
            const stopMs = new Date(nowProgN.stop).getTime();
            const upcoming = all.filter((p) => new Date(p.start).getTime() >= stopMs);
            return upcoming.length > 0 ? upcoming[0] : null;
          })();
          await playWithNativeExoPlayer(url, {
            title,
            contentType: "live_tv",
            // Metadata for the Live TV overlay panel.
            posterUrl: channel?.logo_url || undefined,
            channelGroup: channel?.group || undefined,
            currentEpg: nowProgN
              ? { title: nowProgN.title, start: nowProgN.start, stop: nowProgN.stop }
              : undefined,
            nextEpg: nextProgN
              ? { title: nextProgN.title, start: nextProgN.start, stop: nextProgN.stop }
              : undefined,
            // v1.3.81 — Pass the current category's sibling channels
            // so ExoPlayer can enable D-pad Up/Down zapping without
            // returning to the WebView. `channelStartIndex` positions
            // the queue at the channel the user actually tapped.
            channels: nativeChannelQueue?.queue,
            channelStartIndex: nativeChannelQueue?.startIdx,
          });
        } finally {
          setLaunching(false);
        }
        return;
      }
      // Web fallback
      // v1.3.95 — Pass EPG + zap-queue to the new Live TV overlay in
      // Watch.jsx so it can render the current/next programme line and
      // the UP/DOWN channel-zap chevrons.
      const zapQueue = (channelNav.list || []).map((c) => ({
        id: c.id,
        name: c.name,
        stream_url: c.stream_url,
        logo_url: c.logo_url || "",
        group: c.group || "",
      }));
      const nowProg = channel ? currentProgramme(epgByChannel, channel) : null;
      // "next" = the earliest programme starting after `nowProg.stop`.
      const nextProg = (() => {
        if (!channel || !nowProg?.stop) return null;
        const all = getChannelProgrammes(epgByChannel, channel);
        const stopMs = new Date(nowProg.stop).getTime();
        const upcoming = all.filter((p) => new Date(p.start).getTime() >= stopMs);
        return upcoming.length > 0 ? upcoming[0] : null;
      })();
      navigate("/player/watch", {
        state: {
          stream_url: url,
          name: title,
          logo: channel?.logo_url || "",
          type: "live_tv",
          group: channel?.group || "",
          channel_id: channel?.id || "",
          current_programme: nowProg ? {
            title: nowProg.title, start: nowProg.start, stop: nowProg.stop,
          } : null,
          next_programme: nextProg ? {
            title: nextProg.title, start: nextProg.start, stop: nextProg.stop,
          } : null,
          zap_queue: zapQueue,
        },
      });
    },
    [channel, navigate, nativeChannelQueue, channelNav, epgByChannel]
  );

  // ===== Embedded inline native player =====
  // Place a native ExoPlayer surface directly over a placeholder <div> in
  // the React layout so the live stream renders inline alongside the EPG
  // (no PIP, no separate fullscreen activity).
  const placeholderRef = useRef(null);
  const lastChannelIdRef = useRef(null);

  const measureAndSync = useCallback(async (mode) => {
    const el = placeholderRef.current;
    if (!el || !channel) return;
    const r = el.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) return;
    const rect = { x: r.left, y: r.top, width: r.width, height: r.height };
    if (mode === "start") {
      lastChannelIdRef.current = channel.id;
      addToHistory({
        stream_url: channel.stream_url,
        name: channel.name,
        logo_url: channel.logo_url || null,
        content_type: "live_tv",
      });
      await playInlineNativeExoPlayer(channel.stream_url, rect, {
        title: channel.name,
        contentType: "live_tv",
      });
    } else {
      await updateInlineNativeRect(rect);
    }
  }, [channel]);

  // Start inline playback when the channel changes, and tear down when the
  // route unmounts or the channel changes again. Also stop when the
  // playlist context goes into a fresh load (e.g. user tapped "Update
  // playlist") so we don't leak the SurfaceView while channels reshuffle.
  useEffect(() => {
    if (!channel || !isNativePlayerAvailable()) return;
    let cancelled = false;
    // Make sure any previous surface is gone before we mount a new one.
    stopInlineNativeExoPlayer();
    // Wait a frame so the placeholder has its final size.
    const handle = requestAnimationFrame(() => {
      if (!cancelled) measureAndSync("start");
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(handle);
      stopInlineNativeExoPlayer();
    };
  }, [channel?.id, measureAndSync, channel]);

  // Resume the inline surface when the user returns from the fullscreen
  // ExoPlayer/libVLC activity. Android suspends the WebView while the
  // fullscreen activity is on top, so once the user taps Back the WebView
  // re-shows and `document.visibilitychange` fires with state="visible".
  // Without this the inline placeholder stays black (the surface was torn
  // down in launchExoPlayer to avoid double-audio).
  useEffect(() => {
    if (!channel || !isNativePlayerAvailable()) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Skip if we're mid-launch (the fullscreen activity is still being
      // brought up and will steal the surface anyway).
      if (launching) return;
      // Defer a frame so the WebView has its final layout / rect.
      requestAnimationFrame(() => {
        if (document.visibilityState === "visible" && !launching) {
          measureAndSync("start");
        }
      });
    };
    document.addEventListener("visibilitychange", onVisible);
    // Some Android WebView builds fire `pageshow` but not `visibilitychange`
    // when returning from a foreground activity — listen to both.
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [channel?.id, measureAndSync, launching, channel]);

  // Keep the surface aligned with the placeholder on resize / scroll / orientation.
  useEffect(() => {
    if (!isNativePlayerAvailable()) return;
    const onLayout = () => measureAndSync("update");
    const ro = new ResizeObserver(onLayout);
    if (placeholderRef.current) ro.observe(placeholderRef.current);
    window.addEventListener("scroll", onLayout, true);
    window.addEventListener("resize", onLayout);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", onLayout, true);
      window.removeEventListener("resize", onLayout);
    };
  }, [measureAndSync]);

  const playLive = useCallback(
    () => channel && launchExoPlayer(channel.stream_url, channel.name),
    [channel, launchExoPlayer]
  );

  // v1.4.0 — Windows/Electron inline HLS preview.
  //
  // Previous behaviour auto-launched mpv externally on channel mount,
  // which:
  //   (a) always ate one provider-connection slot even when the user
  //       was just channel-scrolling and never wanted fullscreen;
  //   (b) fought the channel-prefetch HEAD for that slot → 503;
  //   (c) confused the user because the app "flashed" a mpv window
  //       every time they zapped.
  //
  // New behaviour: attach hls.js to a `<video>` inside the page for
  // HLS (.m3u8) streams. This is ONE connection, held only while the
  // user is on this page, and released the instant they zap or leave.
  // For non-HLS URLs (raw MPEGTS .ts) — Chromium can't decode them
  // natively; we show a "Fullscreen only" placeholder and the user
  // presses the Play button to hand off to mpv.
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const mpegtsRef = useRef(null);
  const [inlineError, setInlineError] = useState(null);

  const isHlsStream = useMemo(
    () => channel?.stream_url && /\.m3u8(?:\?|$)/i.test(channel.stream_url),
    [channel?.stream_url]
  );

  useEffect(() => {
    // Only inline-play on Electron / Web (Android uses native).
    if (!channel?.stream_url) return;
    if (isNativePlayerAvailable()) return; // Android: skip, native handles it

    // v1.4.2 — Electron auto-fallback to mpv.
    //
    // hls.js in Chromium only decodes HLS (.m3u8). Every non-HLS URL
    // (raw MPEGTS .ts from Xtream Codes, RTMP, etc.) previously left
    // the user staring at a black video area + a "Press Play" prompt
    // that they had to click before mpv would spawn. On Windows the
    // user complained: "live tv player is not working correct".
    //
    // New contract: on Electron we auto-hand-off to mpv for any
    // stream that can't play inline — either because the URL isn't
    // HLS, or because hls.js emits a fatal MEDIA/NETWORK error while
    // trying to open it. mpv handles everything (AC3/EAC3/HEVC/.ts)
    // and is already the fullscreen player anyway. We add a 400 ms
    // delay so the placeholder briefly announces "Launching player…"
    // instead of a jarring instant black-to-window transition.
    const autoLaunchMpv = () => {
      if (!isElectron()) return;
      // Reuse the same handover logic that `playLiveFullscreen` runs
      // (stops any inline surface, waits 200 ms, then playWithMpv).
      // The `launching` flag guards against re-entrancy.
      setTimeout(() => {
        if (!launchingRef.current) playLiveFullscreenRef.current?.();
      }, 400);
    };

    if (!isHlsStream) {
      // v1.4.7 — On the Web Player (no Electron / mpv fallback available)
      // try mpegts.js for MPEG-TS live streams before giving up. This
      // is the closest thing browsers get to VLC/mpv for raw .ts.
      if (!isElectron() && mpegts.getFeatureList().mseLivePlayback) {
        try {
          const videoEl = videoRef.current;
          if (!videoEl) return;
          setInlineError(null);
          // v1.4.8 — Route the MPEG-TS stream URL through the backend
          // CORS proxy in Web Player mode so cross-origin providers
          // don't get CORS-blocked by the browser.
          const playerUrl = webUrl(channel.stream_url);
          const player = mpegts.createPlayer(
            {
              type: "mpegts",
              isLive: true,
              url: playerUrl,
            },
            {
              enableWorker: true,
              enableStashBuffer: false,
              stashInitialSize: 128,
              liveBufferLatencyChasing: true,
              lazyLoad: false,
            }
          );
          mpegtsRef.current = player;
          player.attachMediaElement(videoEl);
          player.load();
          player.play().catch(() => {/* autoplay blocked */});
          player.on(mpegts.Events.ERROR, (_type, _details, info) => {
            const msg = `MPEG-TS playback error (${info?.msg || _details || _type}). Your browser may not support this codec — use the Android or Windows app for full codec support.`;
            setInlineError(msg);
            autoLaunchMpv();
          });
          return () => {
            try {
              player.unload();
              player.detachMediaElement();
              player.destroy();
            } catch {/* ignore */}
            mpegtsRef.current = null;
          };
        } catch (err) {
          setInlineError(`Unable to start MPEG-TS player: ${err?.message || err}. Try the Android or Windows app.`);
          autoLaunchMpv();
          return;
        }
      }
      setInlineError("MPEG-TS / non-HLS stream — launching fullscreen player…");
      autoLaunchMpv();
      return;
    }
    setInlineError(null);
    const videoEl = videoRef.current;
    if (!videoEl) return;

    let hls;
    if (Hls.isSupported()) {
      hls = new Hls({
        // Live-tuned. Keeps startup fast + memory bounded.
        lowLatencyMode: false,
        enableWorker: true,
        backBufferLength: 30,
        maxBufferLength: 20,
        maxMaxBufferLength: 45,
        // v1.4.8 — In Web Player mode this rewrites every URL (manifest +
        // sub-manifests + segments) through /api/proxy/fetch so CORS
        // doesn't kill playback. Native / same-origin URLs pass through.
        loader: makeProxyLoader(),
      });
      hlsRef.current = hls;
      hls.loadSource(channel.stream_url);
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoEl.play().catch(() => {/* autoplay blocked */});
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          const msg = data.type === "networkError"
            ? `Provider error (${data.details || "network"}) — launching fullscreen player…`
            : `Playback error (${data.details || data.type}) — launching fullscreen player…`;
          setInlineError(msg);
          autoLaunchMpv();
        }
      });
    } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari-style native HLS
      videoEl.src = channel.stream_url;
      videoEl.play().catch(() => {/* autoplay blocked */});
    } else {
      setInlineError("HLS not supported by this browser — launching fullscreen player…");
      autoLaunchMpv();
    }

    return () => {
      if (hls) {
        try {
          hls.destroy();
        } catch {/* ignore */}
      }
      hlsRef.current = null;
      if (videoEl) {
        try {
          videoEl.pause();
          videoEl.removeAttribute("src");
          videoEl.load();
        } catch {/* ignore */}
      }
    };
  }, [channel?.id, channel?.stream_url, isHlsStream]);

  // Kill mpv when the LiveTvWatch page unmounts (user navigated
  // away to Browse / VOD / Series / Settings). Guarantees the
  // provider socket is released the instant the user is no longer
  // watching live TV.
  useEffect(() => {
    return () => {
      if (isElectron() && window.electronAPI?.stopMpv) {
        window.electronAPI.stopMpv().catch(() => {
          /* ignore */
        });
      }
    };
  }, []);

  // When the user presses the fullscreen play button we need to
  // STOP the inline hls.js first (releases the provider slot) then
  // spawn mpv (dials in as the sole connection). Without the stop,
  // hls.js and mpv briefly overlap and the provider 503s the mpv
  // dial-in on 1-connection subscriptions.
  const playLiveFullscreen = useCallback(() => {
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch {/* ignore */}
      hlsRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      } catch {/* ignore */}
    }
    // Small pause so the browser closes the socket before mpv dials.
    setTimeout(() => {
      if (channel) launchExoPlayer(channel.stream_url, channel.name);
    }, 200);
  }, [channel, launchExoPlayer]);
  // Keep the auto-launch ref pointing at the latest callback.
  useEffect(() => { playLiveFullscreenRef.current = playLiveFullscreen; }, [playLiveFullscreen]);

  const playProgramme = (p) => {
    if (!channel) return;
    const nowTs = Math.floor(Date.now() / 1000);
    // Past + xtream archive enabled → catch-up
    if (p.stop_ts <= nowTs && xtream?.creds && channel.xc_id && channel.tv_archive) {
      const built = buildXtreamCatchupUrl(
        xtream.creds,
        channel.xc_id,
        p.start_ts,
        Math.max(1, p.stop_ts - p.start_ts)
      );
      const url = built?.primary || built?.fallback || null;
      if (url) {
        launchExoPlayer(url, `${channel.name} — ${p.title || "Catch-up"}`);
        return;
      }
    }
    // Current → live
    if (p.start_ts <= nowTs && p.stop_ts > nowTs) {
      playLive();
    }
    // Future → ignore
  };

  const onToggleFav = () => {
    if (!channel) return;
    toggleFavorite("live_tv", { ...channel, content_type: "live_tv" });
    setFavTick((t) => t + 1);
  };
  void favTick;

  if (playlists.length === 0) {
    return (
      <div className="py-24 text-center text-slate-400">
        <Tv className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No playlists assigned to this device.</p>
      </div>
    );
  }

  if (!channel) {
    // v1.3.99 — Distinguish between "playlist loading", "playlist
    // load failed" (e.g. provider returned 503), and "playlist loaded
    // fine but the URL param points at a channel that doesn't exist".
    // Previously all three collapsed to "Channel not found" which
    // sent users hunting for a nonexistent bug in our app when the
    // real problem was their provider being down.
    if (status === "loading") {
      return (
        <div className="py-24 text-center text-slate-400" data-testid="livetv-watch-loading">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-70" />
          <p>Loading channels from your provider…</p>
        </div>
      );
    }
    if (status === "error" || (channels.length === 0 && error)) {
      return (
        <div
          className="max-w-lg mx-auto mt-16 rounded-2xl border border-red-500/30 bg-red-500/5 p-6 space-y-3 text-center"
          data-testid="livetv-watch-provider-error"
        >
          <Tv className="w-10 h-10 mx-auto text-red-400" />
          <h3 className="text-lg font-bold text-red-200">
            Couldn&apos;t reach your IPTV provider
          </h3>
          <p className="text-sm text-slate-300">
            {error ? `Error: ${error}` : "The provider didn't respond."}
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            This usually means the provider is temporarily overloaded (HTTP
            503), your subscription hit its device-connection cap, or the
            panel is briefly down. The same channel works on other devices
            when the provider is healthy — try again in a minute, or contact
            your provider if it persists.
          </p>
          <div className="flex items-center justify-center gap-2 pt-1">
            <button
              onClick={() => reload && reload()}
              className="px-4 py-1.5 rounded-md bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold"
              data-testid="livetv-watch-retry"
            >
              Retry
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-slate-200 text-sm"
            >
              Back
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="py-24 text-center text-slate-500 text-sm" data-testid="livetv-watch-channel-missing">
        Channel not found. Go back and pick another.
      </div>
    );
  }

  const nowTs = Math.floor(Date.now() / 1000);

  return (
    <div
      data-testid="player-livetv-watch-screen"
      className="space-y-4"
    >
      {source === "cache-stale" && (
        <div
          className="mx-auto max-w-[1600px] rounded-lg border border-amber-400/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-200 flex items-center gap-2"
          data-testid="livetv-watch-stale-banner"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          <span>
            Provider is temporarily unreachable — showing cached channels.
            Individual streams that are still online will keep playing.
          </span>
          <button
            onClick={() => reload && reload()}
            className="ml-auto px-2 py-0.5 rounded bg-amber-400/20 hover:bg-amber-400/30 text-amber-100 text-[11px] font-semibold"
            data-testid="livetv-watch-stale-retry"
          >
            Retry
          </button>
        </div>
      )}
      {/* Top nav — Prev channel · Back · Next channel */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="livetv-watch-back"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="flex-1" />
        <button
          data-testid="livetv-prev-channel"
          onClick={() =>
            channelNav.prev &&
            navigate(`/player/live/${encodeURIComponent(channelNav.prev.id)}`, {
              replace: true,
            })
          }
          disabled={!channelNav.prev}
          className="text-sm flex items-center gap-1.5 bg-[#11141b] hover:bg-orange-500/15 border border-white/5 hover:border-orange-500/40 disabled:opacity-40 disabled:hover:bg-[#11141b] disabled:hover:border-white/5 text-slate-200 px-3 py-1.5 rounded-md transition-colors"
          title={channelNav.prev ? `Previous: ${channelNav.prev.name}` : "No previous channel"}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="truncate max-w-[120px]">
            {channelNav.prev ? channelNav.prev.name : "—"}
          </span>
        </button>
        <span className="text-[10px] text-slate-500 tabular-nums px-1">
          {channelNav.position} / {channelNav.total}
        </span>
        <button
          data-testid="livetv-next-channel"
          onClick={() =>
            channelNav.next &&
            navigate(`/player/live/${encodeURIComponent(channelNav.next.id)}`, {
              replace: true,
            })
          }
          disabled={!channelNav.next}
          className="text-sm flex items-center gap-1.5 bg-[#11141b] hover:bg-orange-500/15 border border-white/5 hover:border-orange-500/40 disabled:opacity-40 disabled:hover:bg-[#11141b] disabled:hover:border-white/5 text-slate-200 px-3 py-1.5 rounded-md transition-colors"
          title={channelNav.next ? `Next: ${channelNav.next.name}` : "No next channel"}
        >
          <span className="truncate max-w-[120px]">
            {channelNav.next ? channelNav.next.name : "—"}
          </span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div
        className={`grid grid-cols-1 max-w-[1600px] mx-auto ${
          tvLayout
            ? // On TVs the WebView often reports < 1024 CSS px even on a
              // 1080p panel, so we cannot rely on Tailwind's `lg:` (1024px)
              // breakpoint. Force the 2-column split unconditionally.
              "gap-3 grid-cols-[minmax(0,1.6fr)_minmax(260px,1fr)]"
            : "gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]"
        }`}
      >
        {/* ===== LEFT — Embedded live player block ===== */}
        <div className="bg-[#0d1016] border border-white/5 rounded-2xl overflow-hidden">
          {/* This placeholder is the exact rectangle the native ExoPlayer
              surface gets overlaid on. The logo + LIVE pill are visible
              behind the SurfaceView, so on web (where the inline player
              is unavailable) the user still sees branding instead of a
              blank black box. */}
          <div
            ref={placeholderRef}
            data-testid="livetv-inline-surface"
            className="relative aspect-video bg-black flex items-center justify-center"
          >
            {channel.logo_url ? (
              <div className="max-h-[70%] max-w-[70%] opacity-80">
                <PosterImage
                  src={channel.logo_url}
                  alt={channel.name}
                  fallbackIcon={Tv}
                  fallbackIconClassName="w-16 h-16 text-slate-700"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <Tv className="w-16 h-16 text-slate-700" />
            )}
            {/* v1.4.0 — Inline HLS preview on Electron/Web. Uses
                hls.js. Falls back to a "fullscreen only" call-to-
                action for non-HLS streams. Android skips this and
                keeps its native ExoPlayer overlay. */}
            {!isNativePlayerAvailable() && isHlsStream && !inlineError ? (
              <video
                ref={videoRef}
                data-testid="livetv-inline-video"
                className="absolute inset-0 w-full h-full bg-black object-contain"
                playsInline
                controls={false}
                muted={false}
              />
            ) : null}
            {/* "LIVE" pill */}
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-red-600/90 text-white text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md z-10">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Live
            </div>
            {/* Fullscreen button — always visible so the user can
                zoom the current channel out to mpv fullscreen even
                when the inline preview is already running. */}
            {isElectron() && (
              <button
                data-testid="livetv-play-fullscreen"
                onClick={playLiveFullscreen}
                disabled={launching}
                title="Open in mpv fullscreen"
                className="absolute top-3 right-3 z-20 bg-black/70 hover:bg-black text-white p-1.5 rounded-md"
              >
                {launching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </button>
            )}
            {/* Fallback placeholder — non-HLS on Electron/Web, or
                hls.js error. Big centered play button hands off to mpv. */}
            {!isNativePlayerAvailable() && (!isHlsStream || inlineError) && (
              <button
                onClick={playLiveFullscreen}
                disabled={launching}
                title="Play in fullscreen"
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/40 hover:bg-black/60 transition-colors group"
                data-testid="livetv-play-fullscreen-cta"
              >
                <div className="w-20 h-20 rounded-full bg-orange-500 group-hover:bg-orange-400 flex items-center justify-center shadow-lg shadow-orange-500/40 transition-colors">
                  {launching ? (
                    <Loader2 className="w-9 h-9 text-white animate-spin" />
                  ) : (
                    <Play className="w-10 h-10 text-white fill-white ml-1" />
                  )}
                </div>
                <span className="text-white text-sm font-medium drop-shadow-md text-center px-6">
                  {launching
                    ? "Launching player…"
                    : inlineError || "Press Play for fullscreen"}
                </span>
              </button>
            )}
          </div>

          <div className={tvLayout ? "p-3 space-y-2" : "p-5 space-y-3"}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className={`font-bold tracking-tight truncate ${tvLayout ? "text-base" : "text-2xl"}`}>
                  {channel.name}
                </h2>
                {channel.group && (
                  <div className={`text-slate-500 mt-0.5 truncate ${tvLayout ? "text-[10px]" : "text-xs"}`}>
                    {channel.group}
                  </div>
                )}
              </div>
              {showFav && (
                <button
                  data-testid="livetv-toggle-fav"
                  onClick={onToggleFav}
                  aria-pressed={fav}
                  title={fav ? "Remove from favorites" : "Add to favorites"}
                  className={`shrink-0 rounded-full flex items-center justify-center transition-colors ${
                    tvLayout ? "w-7 h-7" : "w-9 h-9"
                  } ${
                    fav
                      ? "bg-amber-400 text-black"
                      : "bg-white/5 text-slate-400 hover:bg-amber-400/20 hover:text-amber-300"
                  }`}
                >
                  <Star className={`${tvLayout ? "w-3 h-3" : "w-4 h-4"} ${fav ? "fill-current" : ""}`} />
                </button>
              )}
            </div>

            {/* Now / Next compact card */}
            {nowProg ? (
              <div className={`rounded-lg border border-emerald-500/30 bg-emerald-500/5 ${tvLayout ? "px-2.5 py-1.5" : "px-3 py-2"}`}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  On now
                </div>
                <div className={`font-semibold text-slate-100 mt-0.5 truncate ${tvLayout ? "text-xs" : "text-sm"}`}>
                  {nowProg.title}
                </div>
                <div className="text-[11px] text-slate-400">
                  {fmtTime(new Date(nowProg.start_ts * 1000))} –{" "}
                  {fmtTime(new Date(nowProg.stop_ts * 1000))}
                </div>
                {nowProg.description && !tvLayout && (
                  <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-3 leading-relaxed">
                    {nowProg.description}
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] text-slate-500">
                No EPG info for this channel.
              </div>
            )}

            <button
              data-testid="livetv-play-live-cta"
              onClick={playLive}
              disabled={launching}
              className={`w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold rounded-lg ${
                tvLayout ? "text-xs py-1.5" : "text-sm py-2.5"
              }`}
            >
              {launching ? (
                <Loader2 className={tvLayout ? "w-3.5 h-3.5 animate-spin" : "w-4 h-4 animate-spin"} />
              ) : (
                <Maximize2 className={tvLayout ? "w-3.5 h-3.5" : "w-4 h-4"} />
              )}
              Watch full-screen
            </button>
          </div>
        </div>

        {/* ===== RIGHT — EPG list ===== */}
        <div className={`bg-[#0d1016] border border-white/5 rounded-2xl overflow-hidden flex flex-col ${tvLayout ? "max-h-[calc(100vh-120px)]" : "max-h-[calc(100vh-160px)]"}`}>
          <div className={`flex items-center justify-between border-b border-white/5 bg-[#0a0c11] ${tvLayout ? "px-3 py-2" : "px-4 py-3"}`}>
            <div className={`flex items-center gap-2 font-semibold ${tvLayout ? "text-xs" : "text-sm"}`}>
              <Calendar className={tvLayout ? "w-3.5 h-3.5 text-orange-400" : "w-4 h-4 text-orange-400"} />
              Programme guide
            </div>
            <div className="text-[10px] text-slate-500">
              {fmtDay(new Date())}
            </div>
          </div>

          {epgWindow.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-sm">
              No EPG available for this channel.
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 px-3 py-3 space-y-5">
              {grouped.map(([day, items]) => (
                <section key={day}>
                  <h3 className="text-[10px] uppercase tracking-wider text-orange-300/80 font-semibold mb-2 px-1">
                    {day}
                  </h3>
                  <div className="space-y-1">
                    {items.map((p) => {
                      const isLive =
                        p.start_ts <= nowTs && p.stop_ts > nowTs;
                      const isPast = p.stop_ts <= nowTs;
                      const isFuture = p.start_ts > nowTs;
                      const canCatchup =
                        isPast &&
                        xtream?.creds &&
                        channel.xc_id &&
                        channel.tv_archive;
                      const disabled = isFuture || (isPast && !canCatchup);
                      const cls = isLive
                        ? "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/40"
                        : canCatchup
                        ? "bg-orange-500/5 hover:bg-orange-500/15 border-orange-500/30"
                        : isPast
                        ? "border-white/5 text-slate-600"
                        : "border-white/10 hover:bg-white/5";
                      const Icon = canCatchup ? History : isLive ? Play : Clock;
                      return (
                        <button
                          key={`${p.start_ts}-${p.title}`}
                          data-testid={`livetv-epg-${p.start_ts}`}
                          disabled={disabled}
                          onClick={() => !disabled && playProgramme(p)}
                          className={`w-full text-left flex items-start gap-3 rounded-lg border transition-colors ${cls} ${
                            disabled ? "cursor-default" : ""
                          } ${tvLayout ? "px-2.5 py-1.5" : "px-3 py-2.5"}`}
                        >
                          <div className={`shrink-0 w-12 tabular-nums text-slate-400 pt-0.5 ${tvLayout ? "text-[10px]" : "text-[11px]"}`}>
                            {fmtTime(new Date(p.start_ts * 1000))}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {isLive && (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded">
                                  Now
                                </span>
                              )}
                              {canCatchup && (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-orange-500/20 text-orange-300 px-1.5 py-0.5 rounded">
                                  Catch-up
                                </span>
                              )}
                              <span className={`font-medium truncate ${tvLayout ? "text-xs" : "text-sm"}`}>
                                {p.title || "Untitled"}
                              </span>
                            </div>
                            {p.description && !tvLayout && (
                              <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                                {p.description}
                              </p>
                            )}
                          </div>
                          <Icon
                            className={`shrink-0 mt-0.5 ${tvLayout ? "w-3.5 h-3.5" : "w-4 h-4"} ${
                              disabled
                                ? "text-slate-700"
                                : isLive
                                ? "text-emerald-300"
                                : canCatchup
                                ? "text-orange-300"
                                : "text-slate-500"
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
